'use client';

import { FC, useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import FadeIn from '@/components/FadeIn/FadeIn';
import { ComparisonData } from '../types';

interface WhySwitchingProps {
  data: ComparisonData;
}

const AUTO_SCROLL_INTERVAL = 4000; // 4 seconds

const StarRating: FC<{ rating: number }> = ({ rating }) => (
  <div className="flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <svg
        key={i}
        className={`w-5 h-5 ${i < rating ? 'text-amber-400' : 'text-slate-300'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ))}
  </div>
);

// Parse quote to render bold text wrapped in **
const renderQuoteWithBold = (quote: string) => {
  const parts = quote.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

const WhySwitching: FC<WhySwitchingProps> = ({ data }) => {
  const { testimonials } = data;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const activeTestimonial = testimonials[activeIndex];

  const goToNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  // Auto-scroll effect
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(goToNext, AUTO_SCROLL_INTERVAL);
    return () => clearInterval(interval);
  }, [isPaused, goToNext]);

  const handleAvatarClick = (index: number) => {
    setActiveIndex(index);
    // Pause auto-scroll briefly when user interacts
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), AUTO_SCROLL_INTERVAL * 2);
  };

  return (
    <section className="py-16 md:py-24">
      <div
        className="container-fluid px-4 md:px-7.5 mx-auto"
        style={{ maxWidth: 'clamp(1000px, 70vw, 1200px)' }}
      >
        <FadeIn variant="up" delay={0.1}>
          {/* Section Title */}
          <div className="text-center mb-12">
            <h2
              className="font-medium text-black"
              style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.5rem)' }}
            >
              Why teams are{' '}
              <span style={{ color: '#094FFE' }}>switching</span>
              <br />
              to marketbetter.ai?
            </h2>
          </div>

          {/* Testimonial Carousel */}
          <div className="flex flex-col md:flex-row gap-6 items-stretch">
            {/* Left: Avatar Stack - Vertical cards */}
            <div className="flex md:flex-col gap-3 flex-shrink-0">
              {testimonials.map((testimonial, index) => {
                const isActive = index === activeIndex;
                return (
                  <button
                    key={index}
                    onClick={() => handleAvatarClick(index)}
                    className={`relative overflow-hidden transition-all duration-300 flex-shrink-0 bg-slate-50 ${
                      isActive
                        ? 'w-56 h-52 md:w-60 md:h-56 rounded-2xl border-[3px] border-blue-500'
                        : 'w-56 h-24 md:w-60 md:h-28 rounded-xl border border-slate-200 hover:border-slate-300'
                    }`}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                  >
                    <Image
                      src={testimonial.avatar}
                      alt={testimonial.author}
                      fill
                      className="object-cover object-top transition-all duration-300"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.author)}&size=240&background=f1f5f9&color=475569&font-size=0.4`;
                      }}
                    />
                  </button>
                );
              })}
            </div>

            {/* Right: Quote Card */}
            <div
              className="flex-1 rounded-2xl p-8 md:p-10"
              style={{ backgroundColor: '#EFF4FF' }}
            >
              <div className="flex flex-col h-full justify-between">
                {/* Quote */}
                <blockquote
                  className="text-slate-800 mb-8"
                  style={{
                    fontSize: 'clamp(1.125rem, 1.5vw, 1.25rem)',
                    lineHeight: '1.8',
                  }}
                >
                  {renderQuoteWithBold(activeTestimonial.quote)}
                </blockquote>

                {/* Author Info */}
                <div className="flex items-end justify-between flex-wrap gap-4">
                  <div>
                    <p className="font-medium text-black text-base">
                      {activeTestimonial.author}
                    </p>
                    <p className="text-slate-500 text-sm">
                      {activeTestimonial.title}
                    </p>
                  </div>

                  {/* Star Rating */}
                  {activeTestimonial.rating && (
                    <StarRating rating={activeTestimonial.rating} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default WhySwitching;
